import React, { useEffect } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'
import { useConfiguratorContext } from '@augmatica/iframe-interface-react'
import {
  setIsEditItemPanelActive,
  setSelectedToolbarTabIndex,
  setSelectedToolbarDoorSubTab,
  setSelectedToolbarRoofSubTab,
  setSelectedToolbarGarageSubTab,
  handleItemInfoModalOpen,
  setViewInside,
} from '../../store/ui/uiSlice'

import EditItemPanel from '../EditItemPanel/EditItemPanel'
import ItemPanel from '../ItemPanel/ItemPanel'
import OptionsPanel from '../OptionsPanel/OptionsPanel'
import RoofCladdingPanel from '../RoofCladdingPanel/RoofCladdingPanel'
import RoofKindPanel from '../RoofKindPanel/RoofKindPanel'
import SizePanel from '../SizePanel/SizePanel'
import LoftPanel from '../LoftPanel/LoftPanel'
import GaragePanel from '../GaragePanel/GaragePanel'
import DoorsPanel from '../DoorsPanel/DoorsPanel'
import SnowZonePanel from '../SnowZonePanel/SnowZonePanel'
import { getFilteredItems } from '../../helpers/getFilteredItems'
import { getInfoModalPayload } from '../../helpers/getInfoModalPayload'
import { isLfcItem as checkLfcItem } from '../../helpers/isLfcItem'
import { getEditorialDataFromSelectedItem } from '../../helpers/getEditorialDataFromSelectedItem'

import { PanelHeader } from '../Heading/styles'
import * as GlobalStyled from '../styles'
import * as Styled from './styles'
import { useAppDispatch, useAppSelector } from '../../hooks'

const Toolbar: React.FC = () => {
  const dispatch = useAppDispatch()

  const configurator = useConfiguratorContext()

  const { meta, props, items } = useAppSelector((state) => state.configResult)
  const { assetBaseUrl, content, langMarketCode } = useAppSelector((state) => state.init)
  const { selectedItem, slotOptions } = useAppSelector((state) => state.selectedItem)
  const { erpItems, renderItems, filterOptions } = useAppSelector((state) => state.items)

  const { isEditItemPanelActive, selectedToolbarTabIndex, selectedToolbarRoofSubTab } = useAppSelector(
    (state) => state.ui
  )

  useEffect(() => {
    switch (selectedItem.item) {
      case 'door':
        dispatch(setSelectedToolbarDoorSubTab(0))
        dispatch(setSelectedToolbarTabIndex(3))
        break

      case 'double-door':
        dispatch(setSelectedToolbarDoorSubTab(1))
        dispatch(setSelectedToolbarTabIndex(3))
        break

      case 'window':
        dispatch(setSelectedToolbarTabIndex(4))
        break

      case 'garage-door':
        dispatch(setSelectedToolbarGarageSubTab(0))
        dispatch(setSelectedToolbarTabIndex(5))
        break

      case 'sliding-door':
        dispatch(setSelectedToolbarGarageSubTab(1))
        dispatch(setSelectedToolbarTabIndex(5))
        break

      case 'facade-section':
        dispatch(setSelectedToolbarGarageSubTab(2))
        dispatch(setSelectedToolbarTabIndex(5))
        break
    }
  }, [selectedItem])

  useEffect(() => {
    const createItemHandler =
      (tabIndex: number, itemFilter: keyof typeof renderItems) =>
      ({ item, id }: any) => {
        dispatch(setSelectedToolbarTabIndex(tabIndex))

        const filteredItems = getFilteredItems(
          renderItems[itemFilter] as any,
          slotOptions as any,
          filterOptions[itemFilter]
        ) // TODO
        const firstOption = filteredItems[0]
        configurator.replaceItem(item, id, firstOption.item, firstOption.props)
      }

    const onAddDoor = createItemHandler(3, 'doors') // TODO: replace index with tab lookup
    const onAddWindow = createItemHandler(4, 'windows') // TODO: replace index with tab lookup

    configurator.addEventListener('add-door', onAddDoor)
    configurator.addEventListener('add-window', onAddWindow)

    return () => {
      configurator.removeEventListener('add-door', onAddDoor)
      configurator.removeEventListener('add-window', onAddWindow)
    }
  }, [dispatch, filterOptions, renderItems, slotOptions])

  const isLfcItem = checkLfcItem(selectedItem as any)
  useEffect(() => {
    const onEditItem = (): void => {
      if (isEditItemPanelActive) return
      configurator.cameraToSelected()
      dispatch(setIsEditItemPanelActive(true))
    }

    const showItemInfo = (): void => {
      const erpItem = erpItems.find((erpItem) => erpItem.Sku === selectedItem.erpItemNumber)

      const attributes = []

      if (erpItem) {
        if (isLfcItem) {
          // only works for single config product in configResult, equivalent to current implementation in lfc-frontend
          selectedItem.props?.configData.configResult.configProducts[0].editorial.attributes.map((attr: any) =>
            attributes.push({
              Title: attr.attributeName,
              Value: attr.attributeValue,
            })
          )
        }
        attributes.push(...erpItem.Attributes)
      }

      const _payload = getEditorialDataFromSelectedItem(selectedItem, renderItems)

      const payload = getInfoModalPayload(_payload, assetBaseUrl, langMarketCode, attributes, erpItem)

      dispatch(handleItemInfoModalOpen(payload))
    }

    configurator.addEventListener('edit-item', onEditItem)
    configurator.addEventListener('show-item-info', showItemInfo)

    return () => {
      configurator.removeEventListener('edit-item', onEditItem)
      configurator.removeEventListener('show-item-info', showItemInfo)
    }
  }, [assetBaseUrl, dispatch, erpItems, renderItems, selectedItem])

  return (
    <Styled.Toolbar
      hasSelectedTab={selectedToolbarTabIndex !== undefined}
      isEditItemPanelActive={isEditItemPanelActive}
    >
      {meta && props && items ? (
        <>
          {isEditItemPanelActive && isLfcItem && <EditItemPanel />}
          <Tabs
            selectedIndex={selectedToolbarTabIndex}
            onSelect={(index) => {
              dispatch(setSelectedToolbarTabIndex(index))
              if (index === 1) {
                configurator.setCameraView('roof')
              }
            }}
          >
            <Styled.TabsContainer>
              <TabList>
                {content.tabs.map((tab: any, index: number) => (
                  <Tab
                    tabIndex={index.toString()}
                    key={tab.title + index}
                    onClick={() =>
                      tab.title === 'Loft' ? dispatch(setViewInside(true)) : dispatch(setViewInside(false))
                    }
                  >
                    <img title={tab.title} alt={tab.title} src={`${assetBaseUrl}${tab.image}`} />
                  </Tab>
                ))}
              </TabList>
            </Styled.TabsContainer>

            <TabPanel>
              <PanelHeader>{content.tabs[0].title}</PanelHeader>
              <SizePanel />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content.tabs[1].title}</PanelHeader>
              <Tabs
                selectedIndex={selectedToolbarRoofSubTab}
                onSelect={(index) => {
                  dispatch(setSelectedToolbarRoofSubTab(index))
                }}
              >
                <GlobalStyled.SubTabsList>
                  <Tab tabIndex="1">{content.props.roofKind.title}</Tab>
                  <Tab tabIndex="2">{content.props.roofCladding.title}</Tab>
                  <Tab tabIndex="3">{content.props.snowZone.title}</Tab>
                </GlobalStyled.SubTabsList>
                <TabPanel>
                  <RoofKindPanel />
                </TabPanel>
                <TabPanel>
                  <RoofCladdingPanel />
                </TabPanel>
                <TabPanel>
                  <SnowZonePanel />
                </TabPanel>
              </Tabs>
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content.tabs[2].title}</PanelHeader>
              <LoftPanel />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content.tabs[3].title}</PanelHeader>
              <DoorsPanel />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content.tabs[4].title}</PanelHeader>
              <ItemPanel itemType="windows" />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content.tabs[5].title}</PanelHeader>
              <GaragePanel />
            </TabPanel>
            <TabPanel>
              <PanelHeader>{content.tabs[6].title}</PanelHeader>
              <OptionsPanel />
            </TabPanel>
          </Tabs>
        </>
      ) : window.innerWidth < 1200 ? (
        <div style={{ padding: '0 15px' }}>
          <div style={{ height: '90px' }} />
          <Skeleton width="100%%" height="55px" />
        </div>
      ) : (
        <Skeleton count={5} width="90%" height="70px" />
      )}
    </Styled.Toolbar>
  )
}

export default Toolbar
